import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTeamsData } from '../useTeamsData';
import { deletedWebTeamsFromPlatform } from '../../utils/globalUtils';

/**
 * Fetches raw team assignments for a feature via `config.apis.getTeams`,
 * applies `config.normalizeTeams` to derive `teamsCountMapping` + `defaultId`,
 * and cleans up assignments for deleted teams (web features only).
 */
export function useTeamData(config) {
  const { teamsLinear, orgCode } = useTeamsData();

  const [teamAssignments, setTeamAssignments] = useState({});
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(
    async ({ silent = false } = {}) => {
      if (!config.apis.getTeams) return;
      if (!silent) setLoading(true);
      try {
        setTeamAssignments(await config.apis.getTeams());
      } catch {
        setTeamAssignments({});
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [config.apis]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);

  const { teamsCountMapping, defaultId } = useMemo(
    () =>
      config.normalizeTeams
        ? config.normalizeTeams(teamAssignments, teamsLinear, orgCode)
        : { teamsCountMapping: {}, defaultId: null },
    [config, teamAssignments, teamsLinear, orgCode]
  );

  // Cleanup deleted web teams — delete assignments for teams that no longer
  // exist in the platform hierarchy.
  useEffect(() => {
    if (
      !config.cleanupDeletedTeams ||
      !config.apis.deleteTeam ||
      !teamsLinear.length ||
      !Object.keys(teamAssignments).length
    )
      return;

    const teamsMap = Object.fromEntries(teamsLinear.map((team) => [team.teamId, team]));
    const deletedTeamIds = deletedWebTeamsFromPlatform(teamsMap, orgCode, teamAssignments);
    if (!deletedTeamIds.length) return;

    Promise.all(deletedTeamIds.map((id) => config.apis.deleteTeam(id)))
      .then(() => fetch())
      .catch(() => {});
  }, [config, teamsLinear, teamAssignments, orgCode, fetch]);
  return {
    teamAssignments,
    teamsCountMapping,
    defaultId,
    loading,
    refetch: fetch
  };
}
